import { useEffect, useRef } from 'react';

const COLORS = ['#377dff', '#34c759', '#ffd166', '#ef476f', '#06d6a0', '#a78bfa', '#ff9f1c'];

type Props = {
  /** Number of pieces. */
  count?: number;
  /** How long the burst runs, in ms. */
  duration?: number;
};

/**
 * Canvas-based confetti burst — a single composited layer driven by one rAF
 * loop, so it stays smooth on mobile WebKit (unlike dozens of animated DOM
 * nodes). Cleans up its loop on unmount.
 */
export function Confetti({ count = 90, duration = 2600 }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const parts = Array.from({ length: count }, () => ({
      x: Math.random() * W,
      y: -20 - Math.random() * H * 0.35,
      vx: (Math.random() - 0.5) * 1.8,
      vy: 2 + Math.random() * 3.2,
      size: 6 + Math.random() * 6,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.32,
      round: Math.random() > 0.6,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    }));

    let raf = 0;
    let start = 0;
    const tick = (now: number) => {
      if (!start) start = now;
      const t = now - start;
      const fade = t > duration - 600 ? Math.max(0, (duration - t) / 600) : 1;

      ctx.clearRect(0, 0, W, H);
      ctx.globalAlpha = fade;
      for (const p of parts) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.045; // gravity
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color ?? '#000';
        if (p.round) {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        }
        ctx.restore();
      }

      if (t < duration) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [count, duration]);

  return <canvas ref={ref} className="pointer-events-none fixed inset-0 z-[55] h-full w-full" />;
}
